import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Button, Form, Input, Modal, Segmented, Select, Space, Tag, Typography, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { employeesAPI, servicesAPI } from '../../api/client';
import { ALLOCATION_LABELS, LICENSE_TYPE_COLORS, LICENSE_TYPE_LABELS } from './constants';

const { Text } = Typography;

const ROLES = ['Admin', 'Member', 'Guest', 'Unlicensed'];

function allocationMode(group, serviceAllocationMode) {
  if (group?.license_type !== 'purchased') return null;
  return serviceAllocationMode || 'named_user';
}

function usesAssignmentCapacityLimit(group, serviceAllocationMode) {
  return group?.license_type === 'purchased'
    && allocationMode(group, serviceAllocationMode) === 'named_user'
    && group?.total_licenses !== null
    && group?.total_licenses !== undefined;
}

function isGroupAvailable(group, serviceAllocationMode) {
  if (!group?.is_active) return false;
  if (!usesAssignmentCapacityLimit(group, serviceAllocationMode)) return true;
  return Number(group.used_licenses || 0) < Number(group.total_licenses || 0);
}

function groupCapacityLabel(group, serviceAllocationMode) {
  if (!usesAssignmentCapacityLimit(group, serviceAllocationMode)) return null;
  return `${Number(group.used_licenses || 0)}/${Number(group.total_licenses || 0)}`;
}

function makeGroupOption({ serviceName, serviceAllocationMode, group, showServiceName }) {
  const mode = allocationMode(group, serviceAllocationMode);
  const capacity = groupCapacityLabel(group, serviceAllocationMode);
  const groupName = group.name || 'Основная';
  const label = showServiceName
    ? `${serviceName}${group.name ? ` — ${groupName}` : ''}`
    : groupName;

  return {
    value: group.id,
    disabled: !isGroupAvailable(group),
    searchLabel: `${serviceName || ''} ${groupName} ${LICENSE_TYPE_LABELS[group.license_type] || ''} ${ALLOCATION_LABELS[mode] || ''}`.trim(),
    label: (
      <Space size={6} wrap>
        <Text>{label}</Text>
        <Tag color={LICENSE_TYPE_COLORS[group.license_type]} style={{ marginInlineEnd: 0, fontSize: 10 }}>
          {LICENSE_TYPE_LABELS[group.license_type]}
        </Tag>
        {mode && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            {ALLOCATION_LABELS[mode] || mode}
          </Text>
        )}
        {capacity && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            {capacity}
          </Text>
        )}
      </Space>
    ),
  };
}

export default function AccessAssignmentModal({
  open,
  onClose,
  onSuccess,
  service = null,
  licenseGroups = null,
  defaultGroupId = null,
}) {
  const [form] = Form.useForm();
  const [licenseType, setLicenseType] = useState('purchased');
  const globalMode = !service?.id;
  const activeServiceGroups = useMemo(
    () => (licenseGroups || []).filter(
      group => group.is_active && group.license_type === licenseType,
    ),
    [licenseGroups, licenseType],
  );
  const hasAvailableServiceGroup = licenseType === 'grey'
    ? !!service?.id
    : activeServiceGroups.some(group => isGroupAvailable(group, service?.allocation_mode));

  const { data: employeesData } = useQuery({
    queryKey: ['employees-active'],
    queryFn: () => employeesAPI.getAll({ is_active: true, limit: 999 }).then(r => r.data),
    enabled: !!open,
  });

  const { data: services = [] } = useQuery({
    queryKey: ['services', { license_type: licenseType }],
    queryFn: () => servicesAPI.getAll({ license_type: licenseType }).then(r => r.data),
    enabled: !!open && globalMode,
  });

  const { data: serviceDetails = [] } = useQuery({
    queryKey: ['services-all-details', licenseType],
    queryFn: async () => Promise.all(
      (services || []).map(item => servicesAPI.getOne(item.id).then(r => r.data)),
    ),
    enabled: !!open && globalMode && services.length > 0,
  });

  useEffect(() => {
    if (!open) return;
    form.resetFields();

    if (globalMode) {
      setLicenseType('purchased');
      form.setFieldsValue({ rows: [{}] });
      return;
    }

    const defaultGroup = activeServiceGroups.find(group => group.id === defaultGroupId);
    const onlyGroup = activeServiceGroups.length === 1 ? activeServiceGroups[0] : null;
    const initialGroup = defaultGroup && isGroupAvailable(defaultGroup, service?.allocation_mode)
      ? defaultGroup
      : onlyGroup && isGroupAvailable(onlyGroup, service?.allocation_mode)
        ? onlyGroup
        : null;
    const target = licenseType === 'grey' && !initialGroup
      ? `service:${service.id}`
      : initialGroup?.id;

    form.setFieldsValue({ license_group_id: target });
  }, [open, globalMode, defaultGroupId, service?.id, activeServiceGroups, licenseType, form]);

  const groupOptions = useMemo(() => {
    if (!globalMode) {
      const options = activeServiceGroups.map(group => makeGroupOption({
        serviceName: service?.name,
        serviceAllocationMode: service?.allocation_mode,
        group,
        showServiceName: false,
      }));

      if (licenseType === 'grey' && options.length === 0 && service?.id) {
        return [{
          value: `service:${service.id}`,
          searchLabel: service.name || '',
          label: (
            <Space size={6} wrap>
              <Text>{service.name}</Text>
              <Tag color={LICENSE_TYPE_COLORS.grey} style={{ marginInlineEnd: 0, fontSize: 10 }}>
                {LICENSE_TYPE_LABELS.grey}
              </Tag>
            </Space>
          ),
        }];
      }
      return options;
    }

    return (serviceDetails || []).flatMap(detail => {
      const matchingGroups = (detail.license_groups || [])
        .filter(group => group.is_active && group.license_type === licenseType);

      if (licenseType === 'grey' && matchingGroups.length === 0) {
        return [{
          value: `service:${detail.id}`,
          searchLabel: detail.name,
          label: (
            <Space size={6} wrap>
              <Text>{detail.name}</Text>
              <Tag color={LICENSE_TYPE_COLORS.grey} style={{ marginInlineEnd: 0, fontSize: 10 }}>
                {LICENSE_TYPE_LABELS.grey}
              </Tag>
            </Space>
          ),
        }];
      }

      const showGroupName = matchingGroups.length > 1;
      return matchingGroups.map(group => makeGroupOption({
        serviceName: detail.name,
        serviceAllocationMode: detail.allocation_mode,
        group: showGroupName ? group : { ...group, name: null },
        showServiceName: true,
      }));
    });
  }, [
    globalMode, activeServiceGroups, service?.id, service?.name, service?.allocation_mode,
    serviceDetails, licenseType,
  ]);


  const assignMutation = useMutation({
    mutationFn: async ({ employee_id, role, notes, targets }) => {
      const results = [];
      for (const target of targets) {
        const isGreyService = String(target).startsWith('service:');
        const payload = {
          employee_id,
          role: role || null,
          notes: notes || null,
          ...(isGreyService
            ? { service_id: Number(String(target).slice(8)), license_type: 'grey' }
            : { license_group_id: target }),
        };
        try {
          await servicesAPI.assign(payload);
          results.push({ ok: true, target });
        } catch (error) {
          results.push({
            ok: false,
            target,
            error: error?.response?.data?.detail || 'Ошибка выдачи доступа',
          });
        }
      }
      return results;
    },
    onSuccess: (results) => {
      const successCount = results.filter(result => result.ok).length;
      const failedCount = results.length - successCount;

      if (failedCount) {
        message.warning(`Выдано: ${successCount}, ошибок: ${failedCount}`);
      } else if (successCount === 1) {
        message.success('Доступ выдан');
      } else {
        message.success(`Выдано доступов: ${successCount}`);
      }

      onClose();
      form.resetFields();
      onSuccess?.();
    },
    onError: (error) => message.error(error?.response?.data?.detail || 'Ошибка'),
  });

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const targets = globalMode
      ? (values.rows || []).map(row => row?.license_group_id).filter(Boolean)
      : [values.license_group_id].filter(Boolean);

    if (!targets.length) {
      message.error(globalMode ? 'Добавьте хотя бы один сервис' : 'Для сервиса нет доступного варианта выдачи');
      return;
    }

    assignMutation.mutate({
      employee_id: values.employee_id,
      role: values.role,
      notes: values.notes,
      targets,
    });
  };

  const employeeOptions = (employeesData?.items || employeesData || []).map(employee => ({
    label: `${employee.full_name}${employee.job_title ? ` — ${employee.job_title}` : ''}`,
    value: employee.id,
  }));

  const groupSelect = (name, placeholder, label = null) => (
    <Form.Item
      name={name}
      style={{ marginBottom: globalMode ? 0 : undefined }}
      label={label}
      rules={[{ required: true, message: globalMode ? 'Выберите сервис' : 'Выберите вариант доступа' }]}
    >
      <Select
        showSearch
        optionFilterProp="searchLabel"
        placeholder={placeholder}
        options={groupOptions}
      />
    </Form.Item>
  );

  return (
    <Modal
      open={open}
      title={globalMode ? 'Выдать доступы' : `Выдать доступ — ${service?.name || ''}`}
      onCancel={() => { onClose(); form.resetFields(); }}
      onOk={handleSubmit}
      okText="Выдать"
      cancelText="Отмена"
      confirmLoading={assignMutation.isPending}
      okButtonProps={{ disabled: !globalMode && !hasAvailableServiceGroup }}
      width={globalMode ? 680 : 560}
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          label={<Text strong>Тип доступа</Text>}
          required
          extra={licenseType === 'purchased'
            ? 'Используем купленные лицензии и их модель доступа.'
            : 'Доступ через грязный домен — без закупки, стоимости и лимита лицензий.'}
        >
          <Segmented
            value={licenseType}
            onChange={(value) => {
              setLicenseType(value);
              form.setFieldsValue(globalMode ? { rows: [{}] } : { license_group_id: undefined });
            }}
            options={[
              { label: 'Купленная', value: 'purchased' },
              { label: 'Грязный домен', value: 'grey' },
            ]}
            block
          />
        </Form.Item>

        <Space style={{ width: '100%' }} size={12} align="start">
          <Form.Item
            name="employee_id"
            label="Сотрудник"
            style={{ flex: 1 }}
            rules={[{ required: true, message: 'Выберите сотрудника' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Кому выдаём"
              options={employeeOptions}
            />
          </Form.Item>
          <Form.Item name="role" label="Роль" style={{ width: 180 }}>
            <Select
              placeholder="Без роли"
              allowClear
              options={ROLES.map(role => ({ label: role, value: role }))}
            />
          </Form.Item>
        </Space>

        {globalMode ? (
          <Form.List name="rows" initialValue={[{}]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Space key={key} align="start" style={{ display: 'flex', marginBottom: 8 }}>
                    <div style={{ width: 540 }}>
                      {groupSelect([name, 'license_group_id'], 'Выберите сервис')}
                    </div>
                    {fields.length > 1 && (
                      <Button danger size="small" onClick={() => remove(name)}>✕</Button>
                    )}
                  </Space>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})}>
                  Добавить сервис
                </Button>
              </>
            )}
          </Form.List>
        ) : (
          <>
            <Form.Item label="Сервис">
              <Input value={service?.name || ''} readOnly />
            </Form.Item>

            {activeServiceGroups.length > 1 ? (
              groupSelect('license_group_id', 'Выберите вариант доступа', 'Вариант доступа')
            ) : (
              <Form.Item name="license_group_id" hidden>
                <Input type="hidden" />
              </Form.Item>
            )}

            {licenseType === 'purchased' && !activeServiceGroups.length && (
              <Text type="danger">Для сервиса нет купленных лицензий.</Text>
            )}
            {licenseType === 'purchased' && activeServiceGroups.length === 1 && !hasAvailableServiceGroup && (
              <Text type="danger">Свободных именных лицензий нет.</Text>
            )}
          </>
        )}

        <Form.Item name="notes" label="Комментарий" style={{ marginTop: globalMode ? 12 : 0 }}>
          <Input.TextArea rows={2} placeholder="Необязательно" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
